import type { Rng } from '../core/rng/Rng';
import { TAU } from '../core/math';
import {
  bfsDistances, carveBrushWalk, carveWobbledEllipse, nearestFloor, stampCircle,
  type GeneratedMap, type Room, type TilePos,
} from './MapGen';

/**
 * Vault side-area ("Reliquary", design §4.2): a short dead-end floor hanging
 * off a normal floor — entry chamber (the return door), a Keeper arena, and
 * an altar alcove. No summon circle, no trash-wave schedule, no shop.
 *
 * Four layouts since ME2. Seeded from the per-origin-floor `vaultMap` stream
 * in a FIXED draw order:
 *   1. layout pick (1 draw; forced layouts still consume it so debug runs
 *      can't shift the stream — the ChunkGen ?mapgen convention)
 *   2. layout-specific draws (each layout's order is fixed within itself)
 *
 * Contract pinned by VaultGen.test for EVERY layout: solid 1-tile border,
 * fully connected, altar BFS-deeper than the arena center, `exit` ===
 * `entrance` (the return door reuses the exit machinery), `spawnRegions` ===
 * [arena], `circleCandidates` empty, ≤40×40. The arena center must be a
 * floor tile ≥350px from the entrance — the Keeper spawns exactly there and
 * wakes at 350px (World.tickKeeper).
 */

export const VAULT_LAYOUTS = ['spine', 'ring', 'cross', 'hall'] as const;
export type VaultLayout = typeof VAULT_LAYOUTS[number];

export interface VaultMap extends GeneratedMap {
  layout: VaultLayout;
  altar: TilePos;
  arena: Room;
}

const VAULT_W = 40;
const VAULT_H = 40;

export function generateVaultMap(rng: Rng, forceLayout?: VaultLayout): VaultMap {
  // Layout pick — draw ALWAYS consumed; forcing can't shift the stream.
  const drawn = rng.pick(VAULT_LAYOUTS);
  const layout = forceLayout ?? drawn;
  switch (layout) {
    case 'spine': return genSpine(rng);
    case 'ring': return genRing(rng);
    case 'cross': return genCross(rng);
    case 'hall': return genHall(rng);
  }
}

/** stampCircle's inverse: writes wall back over floor (the ring's plug). */
function stampWallCircle(tiles: Uint8Array, w: number, h: number, px: number, py: number, r: number): void {
  const minX = Math.max(1, Math.floor(px - r));
  const maxX = Math.min(w - 2, Math.ceil(px + r));
  const minY = Math.max(1, Math.floor(py - r));
  const maxY = Math.min(h - 2, Math.ceil(py + r));
  const r2 = r * r;
  for (let ty = minY; ty <= maxY; ty++) {
    for (let tx = minX; tx <= maxX; tx++) {
      const dx = tx - px;
      const dy = ty - py;
      if (dx * dx + dy * dy <= r2) tiles[ty * w + tx] = 0;
    }
  }
}

function finish(
  tiles: Uint8Array, layout: VaultLayout,
  entrance: TilePos, altar: TilePos, arena: Room, rooms: Room[],
): VaultMap {
  return {
    w: VAULT_W,
    h: VAULT_H,
    archetype: 'vault',
    layout,
    tiles,
    rooms,
    entrance,
    // The return door IS the "exit" — RunScene routes it to exitVault().
    exit: entrance,
    shop: entrance, // unused: vault mode never builds a shop
    circleCandidates: [],
    spawnRegions: [arena],
    pockets: [],
    eventCandidates: [],
    altar,
    arena,
  };
}

// ---------------------------------------------------------------------------
// spine — the original: entry → corridor → arena → corridor → alcove.

function genSpine(rng: Rng): VaultMap {
  const w = VAULT_W;
  const h = VAULT_H;
  const tiles = new Uint8Array(w * h);

  const flip = rng.bool();
  const fx = (x: number): number => (flip ? w - 1 - x : x);

  // Chambers along the spine, with a little vertical drift.
  const spec = [
    { cx: 8, rx: [5, 6] as const, ry: [4.5, 5.5] as const, wobble: [0.1, 0.07] as const },
    { cx: 21, rx: [8, 9] as const, ry: [6.5, 7.5] as const, wobble: [0.05, 0.04] as const },
    { cx: 33, rx: [4, 5] as const, ry: [3.5, 4.5] as const, wobble: [0.1, 0.07] as const },
  ];
  const centers: { cx: number; cy: number; halfW: number; halfH: number }[] = [];
  for (const s of spec) {
    const cy = Math.round(h / 2 + rng.range(-3, 3));
    const rx = rng.range(s.rx[0], s.rx[1]);
    const ry = rng.range(s.ry[0], s.ry[1]);
    const phi1 = rng.range(0, TAU);
    const phi2 = rng.range(0, TAU);
    carveWobbledEllipse(tiles, w, h, fx(s.cx), cy, rx, ry, s.wobble[0], s.wobble[1], phi1, phi2);
    centers.push({
      cx: fx(s.cx), cy,
      halfW: Math.round(0.75 * rx), halfH: Math.round(0.75 * ry),
    });
  }

  // Corridors — brush walks guarantee connectivity by construction.
  const [entry, arenaC, alcove] = centers as [
    typeof centers[0], typeof centers[0], typeof centers[0],
  ];
  carveBrushWalk(tiles, w, h, { x: entry.cx, y: entry.cy }, { x: arenaC.cx, y: arenaC.cy }, rng, 2.5, 3.5, 0.8);
  carveBrushWalk(tiles, w, h, { x: arenaC.cx, y: arenaC.cy }, { x: alcove.cx, y: alcove.cy }, rng, 2.0, 3.0, 0.6);

  const room = (c: typeof centers[0]): Room => ({
    x: c.cx - c.halfW, y: c.cy - c.halfH,
    w: 2 * c.halfW + 1, h: 2 * c.halfH + 1,
    cx: c.cx, cy: c.cy,
  });
  const entryRoom = room(entry);
  const arena = room(arenaC);
  const alcoveRoom = room(alcove);

  return finish(
    tiles, 'spine',
    { x: entry.cx, y: entry.cy }, { x: alcove.cx, y: alcove.cy },
    arena, [arena, entryRoom, alcoveRoom],
  );
}

// ---------------------------------------------------------------------------
// ring — annular arena around a central wall plug; entry at 6 o'clock, the
// alcove hidden at 12 behind the plug. The arena center sits at 3 o'clock so
// the Keeper wakes as the player rounds the ring, not at the door.

function genRing(rng: Rng): VaultMap {
  const w = VAULT_W;
  const h = VAULT_H;
  const tiles = new Uint8Array(w * h);
  const cx = 20;
  const cy = 19;

  const rOut = rng.range(11.5, 12.5);
  const phi1 = rng.range(0, TAU);
  const phi2 = rng.range(0, TAU);
  carveWobbledEllipse(tiles, w, h, cx, cy, rOut, rOut * 0.95, 0.05, 0.04, phi1, phi2);
  const plugR = rng.range(5, 6);
  stampWallCircle(tiles, w, h, cx, cy, plugR);

  // Entry chamber at 6 o'clock.
  const entryR = rng.range(3, 3.8);
  const ep1 = rng.range(0, TAU);
  const ep2 = rng.range(0, TAU);
  carveWobbledEllipse(tiles, w, h, cx, 34, entryR, entryR * 0.85, 0.1, 0.07, ep1, ep2);
  // Alcove at 12, on the far side of the plug.
  const alcR = rng.range(2.6, 3.2);
  const ap1 = rng.range(0, TAU);
  const ap2 = rng.range(0, TAU);
  carveWobbledEllipse(tiles, w, h, cx, 4, alcR, alcR * 0.85, 0.1, 0.07, ap1, ap2);

  // Short spokes into the annulus (brush stays clear of the plug).
  carveBrushWalk(tiles, w, h, { x: cx, y: 34 }, { x: cx, y: cy + 10 }, rng, 2, 3, 0.5);
  carveBrushWalk(tiles, w, h, { x: cx, y: 4 }, { x: cx, y: cy - 10 }, rng, 2, 2.8, 0.5);

  // Keeper holds the 3-o'clock band of the annulus.
  const arena: Room = { x: 26, y: 16, w: 7, h: 7, cx: 29, cy: 19 };
  const entrance = nearestFloor(tiles, w, h, cx, 34);
  const altar = nearestFloor(tiles, w, h, cx, 4);
  const entryRoom: Room = { x: cx - 3, y: 31, w: 7, h: 6, cx, cy: 34 };
  const alcoveRoom: Room = { x: cx - 3, y: 2, w: 7, h: 5, cx, cy: 4 };
  return finish(tiles, 'ring', entrance, altar, arena, [arena, entryRoom, alcoveRoom]);
}

// ---------------------------------------------------------------------------
// cross — arena center, four arms. Entry down one, the alcove down one of the
// other three (rng), stubs on the rest: the door in tells you nothing.

function genCross(rng: Rng): VaultMap {
  const w = VAULT_W;
  const h = VAULT_H;
  const tiles = new Uint8Array(w * h);
  const c = { x: 20, y: 20 };
  const ends = [
    { x: 20, y: 7 }, { x: 33, y: 20 }, { x: 20, y: 33 }, { x: 7, y: 20 },
  ] as const;

  const centerR = rng.range(5.5, 6.5);
  const cp1 = rng.range(0, TAU);
  const cp2 = rng.range(0, TAU);
  carveWobbledEllipse(tiles, w, h, c.x, c.y, centerR, centerR, 0.07, 0.05, cp1, cp2);

  const entryIdx = rng.int(4);
  const rest = [0, 1, 2, 3].filter((i) => i !== entryIdx);
  const alcoveIdx = rest[rng.int(3)]!;
  const entryEnd = ends[entryIdx]!;
  const alcoveEnd = ends[alcoveIdx]!;

  // Chambers first (fixed order), then corridors (fixed order).
  const entryR = rng.range(3, 3.6);
  const ep1 = rng.range(0, TAU);
  const ep2 = rng.range(0, TAU);
  carveWobbledEllipse(tiles, w, h, entryEnd.x, entryEnd.y, entryR, entryR * 0.9, 0.1, 0.07, ep1, ep2);
  const alcR = rng.range(2.6, 3.2);
  const ap1 = rng.range(0, TAU);
  const ap2 = rng.range(0, TAU);
  carveWobbledEllipse(tiles, w, h, alcoveEnd.x, alcoveEnd.y, alcR, alcR * 0.9, 0.1, 0.07, ap1, ap2);

  carveBrushWalk(tiles, w, h, c, entryEnd, rng, 2.2, 3, 0.6);
  carveBrushWalk(tiles, w, h, c, alcoveEnd, rng, 2, 2.8, 0.5);
  for (const i of rest) {
    if (i === alcoveIdx) continue;
    const end = ends[i]!;
    const stub = { x: c.x + 0.6 * (end.x - c.x), y: c.y + 0.6 * (end.y - c.y) };
    carveBrushWalk(tiles, w, h, c, stub, rng, 2, 2.6, 0.5);
  }

  const arena: Room = { x: 15, y: 15, w: 11, h: 11, cx: c.x, cy: c.y };
  const entrance = nearestFloor(tiles, w, h, entryEnd.x, entryEnd.y);
  const altar = nearestFloor(tiles, w, h, alcoveEnd.x, alcoveEnd.y);
  const entryRoom: Room = {
    x: entryEnd.x - 3, y: entryEnd.y - 3, w: 7, h: 7, cx: entryEnd.x, cy: entryEnd.y,
  };
  const alcoveRoom: Room = {
    x: alcoveEnd.x - 3, y: alcoveEnd.y - 3, w: 7, h: 7, cx: alcoveEnd.x, cy: alcoveEnd.y,
  };
  return finish(tiles, 'cross', entrance, altar, arena, [arena, entryRoom, alcoveRoom]);
}

// ---------------------------------------------------------------------------
// hall — one wide low chamber, the Keeper dais mid-hall, the altar in a nook
// carved behind the far end.

function genHall(rng: Rng): VaultMap {
  const w = VAULT_W;
  const h = VAULT_H;
  const tiles = new Uint8Array(w * h);

  const flip = rng.bool();
  const fx = (x: number): number => (flip ? w - 1 - x : x);
  const cy = 20;

  const rx = rng.range(12.5, 13.5);
  const ry = rng.range(5, 6);
  const p1 = rng.range(0, TAU);
  const p2 = rng.range(0, TAU);
  carveWobbledEllipse(tiles, w, h, fx(19), cy, rx, ry, 0.06, 0.04, p1, p2);

  // Nook behind the far end, drifted off-axis.
  const nookY = Math.round(cy + rng.range(-2, 2));
  const nookR = rng.range(2.2, 2.8);
  stampCircle(tiles, w, h, fx(35), nookY, nookR);
  carveBrushWalk(tiles, w, h, { x: fx(31), y: cy }, { x: fx(35), y: nookY }, rng, 1.8, 2.4, 0.4);

  const daisX = fx(20);
  const arena: Room = { x: daisX - 6, y: cy - 4, w: 13, h: 9, cx: daisX, cy };
  const entrance = nearestFloor(tiles, w, h, fx(7), cy);
  const altar = nearestFloor(tiles, w, h, fx(35), nookY);
  const entryRoom: Room = { x: fx(7) - 2, y: cy - 3, w: 5, h: 7, cx: fx(7), cy };
  const alcoveRoom: Room = { x: fx(35) - 2, y: nookY - 2, w: 5, h: 5, cx: fx(35), cy: nookY };
  return finish(tiles, 'hall', entrance, altar, arena, [arena, entryRoom, alcoveRoom]);
}

/** Sanity used by tests: everything reachable from the entrance. */
export function vaultConnected(map: VaultMap): boolean {
  const dist = bfsDistances(map.tiles, map.w, map.h, map.entrance.x, map.entrance.y);
  for (let i = 0; i < map.tiles.length; i++) {
    if (map.tiles[i] === 1 && dist[i]! < 0) return false;
  }
  return true;
}
